import React, { useState, useEffect } from "react";

const IdealWeightCalculator = () => {
  const [weight, setWeight] = useState(70);
  const [height, setHeight] = useState(170);
  const [gender, setGender] = useState("male");
  const [unitSystem, setUnitSystem] = useState("metric");
  const [age, setAge] = useState(25);
  const [bodyFrame, setBodyFrame] = useState("medium");
  const [result, setResult] = useState({
    idealWeight: "0",
    weightRange: "",
    interpretation: "",
    difference: "",
  });

  // Ideal Weight Calculation using multiple formulas
  const calculateIdealWeight = () => {
    let w = parseFloat(weight);
    let h = parseFloat(height);
    let a = parseFloat(age);

    if (isNaN(w) || w <= 0 || isNaN(h) || h <= 0 || isNaN(a) || a <= 0) return;

    // Convert to metric if needed
    let heightCm = unitSystem === "metric" ? h : h * 2.54;
    let weightKg = unitSystem === "metric" ? w : w * 0.453592;
    let heightInches = heightCm / 2.54;

    // Robinson Formula (1983)
    let robinsonWeight;
    if (gender === "male") {
      robinsonWeight = 52 + 1.9 * (heightInches - 60);
    } else {
      robinsonWeight = 49 + 1.7 * (heightInches - 60);
    }

    // Miller Formula (1983)
    let millerWeight;
    if (gender === "male") {
      millerWeight = 56.2 + 1.41 * (heightInches - 60);
    } else {
      millerWeight = 53.1 + 1.36 * (heightInches - 60);
    }

    // Devine Formula (1974)
    let devineWeight;
    if (gender === "male") {
      devineWeight = 50 + 2.3 * (heightInches - 60);
    } else {
      devineWeight = 45.5 + 2.3 * (heightInches - 60);
    }

    // Hamwi Formula (1964)
    let hamwiWeight;
    if (gender === "male") {
      hamwiWeight = 48 + 2.7 * (heightInches - 60);
    } else {
      hamwiWeight = 45.5 + 2.2 * (heightInches - 60);
    }

    // Average of all formulas
    const averageIdealWeight = (robinsonWeight + millerWeight + devineWeight + hamwiWeight) / 4;

    // Adjust for body frame
    let frameAdjustment = 1;
    if (bodyFrame === "small") {
      frameAdjustment = 0.9;
    } else if (bodyFrame === "large") {
      frameAdjustment = 1.1;
    }

    const adjustedIdealWeight = averageIdealWeight * frameAdjustment;

    // Calculate healthy weight range (BMI 18.5 - 24.9)
    const heightM = heightCm / 100;
    const minHealthyWeight = 18.5 * Math.pow(heightM, 2);
    const maxHealthyWeight = 24.9 * Math.pow(heightM, 2);

    const weightRange = unitSystem === "metric" 
      ? `${minHealthyWeight.toFixed(1)}kg - ${maxHealthyWeight.toFixed(1)}kg`
      : `${(minHealthyWeight * 2.20462).toFixed(1)}lbs - ${(maxHealthyWeight * 2.20462).toFixed(1)}lbs`;

    // Calculate difference from current weight
    const difference = weightKg - adjustedIdealWeight;
    let differenceText = "";
    
    if (Math.abs(difference) < 2) {
      differenceText = "You are at your ideal weight! Maintain your current lifestyle with balanced nutrition and regular exercise.";
    } else if (difference > 0) {
      const diffDisplay = unitSystem === "metric" 
        ? `${Math.abs(difference).toFixed(1)}kg`
        : `${(Math.abs(difference) * 2.20462).toFixed(1)}lbs`;
      differenceText = `You are ${diffDisplay} above your ideal weight. Consider a balanced diet and regular physical activity to reach your goal safely.`;
    } else {
      const diffDisplay = unitSystem === "metric" 
        ? `${Math.abs(difference).toFixed(1)}kg`
        : `${(Math.abs(difference) * 2.20462).toFixed(1)}lbs`;
      differenceText = `You are ${diffDisplay} below your ideal weight. Focus on nutrient-rich foods and strength training to build healthy body mass.`;
    }

    let interpretation = "";
    if (gender === "male") {
      interpretation = "Your ideal weight is calculated using multiple scientific formulas (Robinson, Miller, Devine, and Hamwi) and adjusted for your body frame. Men typically have higher ideal weights due to greater muscle mass.";
    } else {
      interpretation = "Your ideal weight is calculated using multiple scientific formulas (Robinson, Miller, Devine, and Hamwi) and adjusted for your body frame. This accounts for typical female body composition.";
    }

    const idealWeightDisplay = unitSystem === "metric"
      ? `${adjustedIdealWeight.toFixed(1)}kg`
      : `${(adjustedIdealWeight * 2.20462).toFixed(1)}lbs`;

    setResult({
      idealWeight: idealWeightDisplay,
      weightRange,
      interpretation,
      difference: differenceText,
    });
  };

  useEffect(() => {
    calculateIdealWeight();
  }, []);

  return (
    <div className="calculator-wrapper">
      <div className="calculator-container">
        {/* Header */}
        <div className="calculator-header">
          <div className="header-icon">
            <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ color: "white" }}>
              <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
              <circle cx="12" cy="7" r="4"></circle>
            </svg>
          </div>
          <h1>Ideal Weight Calculator</h1>
          <p>
            Calculate your ideal body weight based on scientifically validated formulas that consider your height, gender, age, and body frame for accurate personalized results.
          </p>
        </div>

        {/* Main Calculator Card */}
        <div className="calculator-card">
          <div className="calculator-grid">
            {/* Left Side - Input Section */}
            <div className="input-section">
              <h2 className="section-title">
                <svg className="section-icon" xmlns="http://www.w3.org/2000/svg" width="24" height="24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                  <polyline points="7 10 12 15 17 10"></polyline>
                  <line x1="12" y1="15" x2="12" y2="3"></line>
                </svg>
                Your Information
              </h2>

              {/* Current Weight Input */}
              <div className="input-group">
                <label>Current Weight ({unitSystem === "metric" ? "kg" : "lbs"})</label>
                <div className="input-wrapper">
                  <span className="input-prefix">⚖️</span>
                  <input
                    type="number"
                    value={weight}
                    onChange={(e) => setWeight(e.target.value)}
                    className="calc-input"
                    placeholder="70"
                  />
                </div>
                <div className="quick-select-group">
                  {[50, 60, 70, 80, 90].map((val) => (
                    <button
                      key={val}
                      onClick={() => setWeight(val)}
                      className="quick-select-btn primary"
                    >
                      {val}
                    </button>
                  ))}
                </div>
              </div>

              {/* Height Input */}
              <div className="input-group">
                <label>Height ({unitSystem === "metric" ? "cm" : "in"})</label>
                <div className="input-wrapper">
                  <span className="input-prefix">📏</span>
                  <input
                    type="number"
                    value={height}
                    onChange={(e) => setHeight(e.target.value)}
                    className="calc-input"
                    placeholder="170"
                  />
                </div>
                <div className="quick-select-group">
                  {[150, 160, 170, 180, 190].map((h) => (
                    <button
                      key={h}
                      onClick={() => setHeight(h)}
                      className="quick-select-btn success"
                    >
                      {h}
                    </button>
                  ))}
                </div>
              </div>

              {/* Age */}
              <div className="input-group">
                <label>Age</label>
                <div className="input-wrapper">
                  <span className="input-prefix">🎂</span>
                  <input
                    type="number"
                    value={age}
                    onChange={(e) => setAge(e.target.value)}
                    className="calc-input"
                    placeholder="25"
                    min="1"
                  />
                </div>
              </div>

              {/* Gender */}
              <div className="input-group">
                <label>Gender</label>
                <div className="quick-select-group">
                  {["male", "female"].map((g) => (
                    <button
                      key={g}
                      onClick={() => setGender(g)}
                      className={`quick-select-btn ${gender === g ? "primary" : "secondary"}`}
                    >
                      {g.charAt(0).toUpperCase() + g.slice(1)}
                    </button>
                  ))}
                </div>
              </div>

              {/* Body Frame */}
              <div className="input-group">
                <label>Body Frame</label>
                <div className="quick-select-group">
                  {[
                    { value: "small", label: "Small Frame" },
                    { value: "medium", label: "Medium Frame" },
                    { value: "large", label: "Large Frame" },
                  ].map((frame) => (
                    <button
                      key={frame.value}
                      onClick={() => setBodyFrame(frame.value)}
                      className={`quick-select-btn ${bodyFrame === frame.value ? "primary" : "secondary"}`}
                    >
                      {frame.label}
                    </button>
                  ))}
                </div>
                <p style={{ fontSize: "0.75rem", color: "#6b7280", marginTop: "0.5rem" }}>
                  Wrap your thumb and middle finger around your wrist. If they overlap, you have a small frame. If they just touch, medium frame. If they don't touch, large frame.
                </p>
              </div>

              {/* Unit System */}
              <div className="input-group">
                <label>Measurement System</label>
                <div className="quick-select-group">
                  {[
                    { value: "metric", label: "Metric (kg/cm)" },
                    { value: "imperial", label: "Imperial (lbs/in)" },
                  ].map((unit) => (
                    <button
                      key={unit.value}
                      onClick={() => setUnitSystem(unit.value)}
                      className={`quick-select-btn ${unitSystem === unit.value ? "primary" : "secondary"}`}
                    >
                      {unit.label}
                    </button>
                  ))}
                </div>
              </div>

              <button onClick={calculateIdealWeight} className="calculate-btn">
                Calculate Ideal Weight
              </button>
            </div>

            {/* Right Side - Results Section */}
            <div className="result-section">
              <h2 className="section-title">
                <svg className="section-icon" xmlns="http://www.w3.org/2000/svg" width="24" height="24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="12" r="10"></circle>
                  <path d="M12 8v4l3 2"></path>
                </svg>
                Your Ideal Weight
              </h2>

              <div className="result-card">
                {/* Ideal Weight Value */}
                <div className="result-item highlight">
                  <div className="result-item-header">
                    <span className="result-label">Your Ideal Weight</span>
                    <div className="result-icon-wrapper blue">
                      <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
                        <circle cx="12" cy="7" r="4"></circle>
                      </svg>
                    </div>
                  </div>
                  <div className="result-value">{result.idealWeight}</div>
                  <div className="result-subtext">based on multiple formulas</div>
                </div>

                {/* Healthy Weight Range */}
                <div className="result-item">
                  <div className="result-item-header">
                    <span className="result-label">Healthy Weight Range</span>
                    <div className="result-icon-wrapper green">
                      <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" fill="none" stroke="#10b981" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <polyline points="20 6 9 17 4 12"></polyline>
                      </svg>
                    </div>
                  </div>
                  <div className="result-value">{result.weightRange}</div>
                  <div className="result-subtext">BMI 18.5 - 24.9 range</div>
                </div>

                {/* Weight Difference */}
                <div className="breakdown-section" style={{ marginTop: "1rem" }}>
                  <h3 className="breakdown-title">Weight Assessment</h3>
                  <p style={{ fontSize: "0.9rem", color: "#065f46", fontWeight: "500" }}>
                    {result.difference}
                  </p>
                </div>

                {/* Interpretation */}
                <div className="breakdown-section" style={{ marginTop: "1rem" }}>
                  <h3 className="breakdown-title">How It's Calculated</h3>
                  <p style={{ fontSize: "0.9rem", color: "#065f46" }}>
                    {result.interpretation}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Info Section */}
        <div className="info-section">
          <div className="info-header">
            <div className="info-icon-wrapper">
              <svg className="info-icon" xmlns="http://www.w3.org/2000/svg" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="10"></circle>
                <line x1="12" y1="16" x2="12" y2="12"></line>
                <line x1="12" y1="8" x2="12.01" y2="8"></line>
              </svg>
            </div>
            <div>
              <h3>Understanding Ideal Weight</h3>
              <p>
                Ideal body weight (IBW) is an estimate of the optimal weight for your height and gender based on scientific research. This calculator uses four validated formulas: Robinson (1983), Miller (1983), Devine (1974), and Hamwi (1964).
                These formulas were developed through extensive population studies and are widely used in medical settings. Your result is the average of all four formulas, adjusted for your body frame size. Remember that ideal weight is a guideline, not an absolute target.
              </p>
            </div>
          </div>

          <div className="formula-section">
            <h4>Ideal Weight Formulas Used:</h4>
            <div className="formula-display">
              Robinson (1983): Most widely used modern formula
            </div>
            <div className="formula-display" style={{ marginTop: "0.5rem" }}>
              Miller (1983): Accounts for height variations
            </div>
            <div className="formula-display" style={{ marginTop: "0.5rem" }}>
              Devine (1974): Originally for drug dosing calculations
            </div>
            <div className="formula-display" style={{ marginTop: "0.5rem" }}>
              Hamwi (1964): Classic formula, still widely referenced
            </div>

            <div className="formula-params">
              <div className="formula-param">
                <div className="formula-param-key">Small Frame</div>
                <div className="formula-param-value">-10% adjustment</div>
              </div>
              <div className="formula-param">
                <div className="formula-param-key">Medium Frame</div>
                <div className="formula-param-value">No adjustment</div>
              </div>
              <div className="formula-param">
                <div className="formula-param-key">Large Frame</div>
                <div className="formula-param-value">+10% adjustment</div>
              </div>
              <div className="formula-param">
                <div className="formula-param-key">Base Height</div>
                <div className="formula-param-value">60 inches (152cm)</div>
              </div>
            </div>
          </div>

          {/* FAQ Section */}
          <div className="faq-section">
            <h3>Frequently Asked Questions</h3>

            <div className="faq-item">
              <h5>How accurate is ideal weight calculation?</h5>
              <p>
                Ideal weight formulas provide good estimates for most people but don't account for muscle mass, bone density, or individual health conditions. Use it as a guideline.
              </p>
            </div>

            <div className="faq-item">
              <h5>What if I'm muscular or athletic?</h5>
              <p>
                Athletes and bodybuilders may weigh more than their ideal weight due to muscle mass. In such cases, body composition analysis is more relevant than weight alone.
              </p>
            </div>

            <div className="faq-item">
              <h5>Should I aim for my exact ideal weight?</h5>
              <p>
                Not necessarily. Any weight within the healthy range is acceptable. Focus on overall health, fitness, and how you feel rather than an exact number.
              </p>
            </div>

            <div className="faq-item">
              <h5>How do I determine my body frame?</h5>
              <p>
                Wrap your thumb and middle finger around your wrist. If they overlap, you're small-framed. If they touch, medium-framed. If they don't meet, large-framed.
              </p>
            </div>

            <div className="faq-item">
              <h5>Does ideal weight change with age?</h5>
              <p>
                These formulas are based on adult populations. Metabolism and body composition change with age, so maintaining health becomes more important than hitting an exact weight.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default IdealWeightCalculator;